import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Mountain, Calendar, ArrowUpRight, Images } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { treks } from "../data/content";

export default function TrekTravel() {
  return (
    <div data-testid="page-trek-travel">
      <PageHeader
        eyebrow="Trek & Travel"
        title="Boots worn in, journals filling up."
        intro="Walks in the Himalayas from the last three years. Each panel opens onto a gallery of photographs and a short note from the trail. New treks are added as they happen — two or three a year, usually."
        testid="trek-header"
      />

      <section className="pb-24">
        <div className="container-narrow">
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6" data-testid="trek-grid">
            {treks.map((t, i) => (
              <motion.article
                key={t.slug}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className="card-lift group rounded-sm overflow-hidden bg-[#F2EFEB] border border-[#D8D3CC] flex flex-col"
                data-testid={`trek-card-${i}`}
              >
                <Link
                  to={`/trek-and-travel/${t.slug}`}
                  data-testid={`trek-link-${t.slug}`}
                  className="flex flex-col h-full"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={t.coverImage}
                      alt={`${t.name}, ${t.region}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#FAF9F6]/90 backdrop-blur px-2.5 py-1 rounded-sm text-[11px] tracking-wider text-[#2C2A28] uppercase">
                      <Mountain size={12} strokeWidth={1.6} />
                      {t.elevation}
                    </div>
                    {t.gallery?.length > 0 && (
                      <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[#2C2A28]/80 backdrop-blur px-2.5 py-1 rounded-sm text-[11px] tracking-wider text-[#FAF9F6] uppercase">
                        <Images size={12} strokeWidth={1.6} />
                        {t.gallery.length} photos
                      </div>
                    )}
                  </div>

                  <div className="p-6 sm:p-7 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl sm:text-3xl text-[#2C2A28] leading-tight">
                      {t.name}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#5C5852]">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={12} strokeWidth={1.6} className="text-[#4A6B53]" />
                        {t.region}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={12} strokeWidth={1.6} className="text-[#4A6B53]" />
                        {t.month}
                      </span>
                    </div>
                    <p className="mt-4 text-[#2C2A28] leading-relaxed flex-1">
                      {t.shortDescription}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-1.5 text-[#4A6B53] text-sm tracking-wide group-hover:gap-2.5 transition-all duration-300">
                      View gallery
                      <ArrowUpRight size={14} strokeWidth={1.8} />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>

          <p className="mt-14 text-sm text-[#5C5852] italic max-w-xl">
            Two or three treks a year, give or take. Each one gets its own page here
            — photographs, videos, and a few notes from the trail.
          </p>
        </div>
      </section>
    </div>
  );
}
